import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import store from "@/store";
import Drawer from "@/components/drawer/Drawer.vue";
import HeaderComponent from "@/components/header/Header.vue";
import SignalR from "@/components/signalR/SignalR.vue";
import MediaDeviceNotSupported from "@/components/media-device-not-supported/MediaDeviceNotSupported.vue";
import { faviconSrcUrl } from "@/extensions/extra";
import HandleCache from "../../HandleCache.vue";
import FeatureFlagProvider from "@/plugins/launchDarkly/FeatureFlagProvider.vue";
import { FeatureFlagsService } from "@/plugins/launchDarkly/featureFlags.service";
import CookiesBanner from "@/components/cookies-banner/CookiesBanner.vue";
import { useCookieWatcher } from "@/composables/useCookieWatcher";
import {
  ANALYTICS_COOKIE_NAME,
  CLARITY_COOKIE_NAME,
  getConsentPreferences,
  hasConsentDecision,
  saveConsentDecision,
} from "@/services/cookies/consent.service";
import { applyAnalyticsConsent } from "@/plugins/analytics";
import { clarityService } from "@/services/clarity/clarity.service";

const cacheBuster = ref();

export default defineComponent({
  name: "App",
  components: {
    CookiesBanner,
    Drawer,
    FeatureFlagProvider,
    HeaderComponent,
    HandleCache,
    MediaDeviceNotSupported,
    SignalR,
  },
  setup() {
    const route = useRoute();
    const isLoggedIn = computed(() => store.state.isLoggedIn);
    const loadingSpinner = computed(() => store.state.loadingSpinner);
    const profileLoading = computed(() => store.state.profileLoading);
    const isAuthenticated = computed(() => store.state.isAuthenticated);
    const is401 = computed(() => store.state.is401);
    const auth0User = computed(() => store.state.auth0User);
    const isCookiesBannerVisible = ref(!hasConsentDecision());
    const analyticsCookieValue = useCookieWatcher(ANALYTICS_COOKIE_NAME);
    const clarityCookieValue = useCookieWatcher(CLARITY_COOKIE_NAME);

    const featureFlagsService = FeatureFlagsService.getInstance();
    const ldContext = computed(() => {
      return featureFlagsService.buildUserContext(store.state.auth0User, isAuthenticated.value);
    });

    onMounted(() => {
      changeFavicon();
      applyCookiePreferences();
    });

    watch([analyticsCookieValue, clarityCookieValue], handleConsentCookieChange);
    watch([auth0User, isAuthenticated], identifyCurrentUserInClarity);

    function changeFavicon() {
      let favicon;
      favicon = "";
      favicon = document.createElement("link");
      favicon.type = "image/png";
      favicon.rel = "icon";
      favicon.href = faviconSrcUrl;
      document.getElementsByTagName("head")[0].appendChild(favicon);
    }

    function acceptAllCookies(): void {
      saveConsentDecision("all");
      isCookiesBannerVisible.value = false;
      applyCookiePreferences();
    }

    function acceptEssentialCookies(): void {
      saveConsentDecision("essential");
      isCookiesBannerVisible.value = false;
      applyCookiePreferences();
    }

    function applyCookiePreferences(): void {
      applyAnalyticsConsent(route.fullPath);

      const cookiePreferences = getConsentPreferences();

      if (cookiePreferences.clarity) {
        clarityService.enableTracking();
        identifyCurrentUserInClarity();
        return;
      }

      clarityService.disableTracking();
    }

    function handleConsentCookieChange(): void {
      isCookiesBannerVisible.value = !hasConsentDecision();
      applyCookiePreferences();
    }

    function navigateCookieSettings(): void {
      window.open("https://vaultn.com/cookie-settings", "_blank", "noopener,noreferrer");
    }

    function identifyCurrentUserInClarity(): void {
      if (!isAuthenticated.value || !auth0User.value || !getConsentPreferences().clarity) {
        return;
      }

      clarityService.identifyAuthenticatedUser(auth0User.value);
    }

    return {
      acceptAllCookies,
      acceptEssentialCookies,
      changeFavicon,
      isCookiesBannerVisible,
      isLoggedIn,
      loadingSpinner,
      profileLoading,
      isAuthenticated,
      is401,
      cacheBuster,
      ldContext,
      navigateCookieSettings,
    };
  },
});
